'use client';

import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Skeleton } from '@kit/ui/skeleton';

/**
 * Skeleton placeholder matching the interaction results layout.
 * Shown during the cache probe on URL-sync landings.
 */
export function ResultsSkeleton() {
  const { t } = useTranslation('interactions');

  return (
    <div className="mt-6 border-t-4 border-dashed border-zinc-300 pt-10 dark:border-zinc-600">
      {/* Matches <div className="space-y-4"> in interaction-results.tsx:278 */}
      <div className="space-y-4">
        {/* Summary card — matches InteractionsSummaryCard outer <div> */}
        <div className="@container">
          {/* Title section — matches summary-card.tsx mb-2 wrapper */}
          <div className="mb-2">
            <h2 className="text-2xl font-semibold tracking-tight text-zinc-700 dark:text-zinc-200">
              {t('summary.title')}
            </h2>
            <Skeleton className="mt-1 h-4 w-48" />
          </div>

          {/* Corner tab severity rows */}
          <div className="md:flex">
            <CornerTabRowSkeleton
              bg="bg-red-100 dark:bg-red-900/40"
              border="border-red-200 dark:border-red-800"
              dot="bg-red-400 dark:bg-red-600"
            >
              <PairBadgeSkeleton
                bg="bg-red-100 dark:bg-red-950"
                border="border-red-300 dark:border-red-700"
              />
            </CornerTabRowSkeleton>
            <CornerTabRowSkeleton
              bg="bg-orange-100 dark:bg-orange-900/40"
              border="border-orange-200 dark:border-orange-800"
              dot="bg-orange-400 dark:bg-orange-600"
            >
              <PairBadgeSkeleton
                bg="bg-orange-100 dark:bg-orange-950"
                border="border-orange-300 dark:border-orange-700"
              />
              <PairBadgeSkeleton
                bg="bg-orange-100 dark:bg-orange-950"
                border="border-orange-300 dark:border-orange-700"
              />
            </CornerTabRowSkeleton>
            <CornerTabRowSkeleton
              bg="bg-yellow-100 dark:bg-yellow-900/40"
              border="border-yellow-200 dark:border-yellow-800"
              dot="bg-yellow-400 dark:bg-yellow-600"
            />
            <CornerTabRowSkeleton
              bg="bg-blue-100 dark:bg-blue-900/40"
              border="border-blue-200 dark:border-blue-800"
              dot="bg-blue-400 dark:bg-blue-600"
            />
          </div>
        </div>

        {/* Cards section — matches mt-8 space-y-4 in interaction-results.tsx:323 */}
        <div className="mt-10 space-y-4">
          <div className="flex items-center justify-center gap-3">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-40" />
          </div>
          <CardSkeleton
            border="border-red-300 dark:border-red-700"
            bg="bg-red-50 dark:bg-red-950/30"
            dot="bg-red-400 dark:bg-red-600"
            badge="bg-red-200 dark:bg-red-800/60"
          />
          <CardSkeleton
            border="border-orange-300 dark:border-orange-700"
            bg="bg-orange-50 dark:bg-orange-950/30"
            dot="bg-orange-400 dark:bg-orange-600"
            badge="bg-orange-200 dark:bg-orange-800/60"
          />
        </div>
      </div>
    </div>
  );
}

function CornerTabRowSkeleton({
  bg,
  border,
  dot,
  children,
}: {
  bg: string;
  border: string;
  dot: string;
  children?: ReactNode;
}) {
  const hasPairs = !!children;
  return (
    <div
      className={`border-t border-l-4 leading-[0] md:flex md:flex-1 md:flex-col md:items-start md:pr-3 md:leading-normal ${border} ${hasPairs ? 'pb-4 md:pb-2' : ''}`}
    >
      <div
        className={`inline-flex animate-pulse items-center gap-1.5 py-1 pr-3 pl-3 text-[11px] leading-normal md:self-start md:rounded-r-md ${bg} ${hasPairs ? 'mb-4 md:mb-2' : ''}`}
      >
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <span className="h-3 w-6 rounded bg-current opacity-10" />
        <span className="h-3 w-12 rounded bg-current opacity-10" />
      </div>
      {hasPairs && <div className="flex flex-wrap gap-2 pl-4">{children}</div>}
    </div>
  );
}

function PairBadgeSkeleton({ bg, border }: { bg: string; border: string }) {
  return (
    <div
      className={`inline-flex animate-pulse items-center gap-1 rounded-md border-2 px-2.5 py-1 ${bg} ${border}`}
    >
      <span className="h-3 w-16 rounded bg-current opacity-10" />
      <span className="text-xs opacity-30">+</span>
      <span className="h-3 w-20 rounded bg-current opacity-10" />
    </div>
  );
}

function CardSkeleton({
  border,
  bg,
  dot,
  badge,
}: {
  border: string;
  bg: string;
  dot: string;
  badge: string;
}) {
  return (
    <div className={`overflow-hidden rounded-lg border-2 ${border} ${bg}`}>
      {/* Matches interaction-card.tsx: <div className="relative p-4"> */}
      <div className="relative p-4">
        {/* Severity badge — matches mb-3 gap-2 pr-8 */}
        <div className="mb-3 flex items-center gap-2 pr-8">
          <span
            className={`h-5 w-5 animate-pulse rounded-full opacity-60 ${dot}`}
          />
          <Skeleton className={`h-5 w-20 rounded-full ${badge}`} />
        </div>
        {/* Drug pair names — matches flex-col gap-0.5 @[640px]:flex-row @[640px]:gap-2 */}
        <div className="flex flex-col gap-0.5 @[640px]:flex-row @[640px]:flex-wrap @[640px]:items-center @[640px]:gap-2">
          <Skeleton className="h-5 w-28" />
          <div className="flex items-center gap-1.5 @[640px]:contents">
            <Skeleton className="h-3.5 w-3.5 @[640px]:h-4 @[640px]:w-4" />
            <Skeleton className="h-5 w-32" />
          </div>
        </div>
        {/* Summary — matches mt-4 with flex items-start gap-2 and icon */}
        <div className="mt-4">
          <div className="flex items-start gap-2">
            <Skeleton className="mt-1 h-4 w-4 shrink-0 rounded" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
